import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ScreenSize } from "@circle-react-shared/ScreenSizeSelect";

const DeviceContext = createContext<ScreenSize>("desktop");
DeviceContext.displayName = "DeviceContext";

const getDeviceFromWidth = (width: number): ScreenSize => {
  if (width <= 768) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
};

export const useDeviceContext = (): ScreenSize => useContext(DeviceContext);

interface DeviceProviderProps {
  canvasSize?: ScreenSize;
  children: ReactNode;
  isPreviewMode?: boolean;
}

export const DeviceProvider = ({
  children,
  isPreviewMode = false,
  canvasSize = "desktop",
}: DeviceProviderProps) => {
  const [device, setDevice] = useState<ScreenSize>(() => {
    if (typeof window === "undefined") return "desktop";
    return getDeviceFromWidth(window.innerWidth);
  });

  const updateDevice = useCallback(() => {
    const width = window.innerWidth;
    const newDevice = getDeviceFromWidth(width);
    setDevice(prev => (prev !== newDevice ? newDevice : prev));
  }, []);

  useEffect(() => {
    if (isPreviewMode || typeof window === "undefined") return;

    updateDevice();

    const resizeObserver = new ResizeObserver(updateDevice);
    resizeObserver.observe(document.documentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isPreviewMode, updateDevice]);

  useEffect(() => {
    if (isPreviewMode) {
      setDevice(canvasSize);
    }
  }, [isPreviewMode, canvasSize]);

  return (
    <DeviceContext.Provider value={device}>{children}</DeviceContext.Provider>
  );
};
